import { useState } from "react";
import { toast } from "sonner";
import { Mail, Clock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import useSEO from "@/hooks/useSEO";
import { PageHero } from "@/components/ui-lux";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Contact() {
  const { settings } = useSite();
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const faqs = settings?.faqs || [];
  useSEO({ title: "Contact", description: "Contact The Transformed Mind Life team.",
    jsonLd: faqs.length ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })) } : null });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      toast.success("Message sent. We'll be in touch soon.");
      setF({ name: "", email: "", subject: "", message: "" });
    } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Let's talk." sub="Questions about membership, coaching, events or speaking invitations? Send a message." />
      <div className="container-x grid lg:grid-cols-[1.3fr_1fr] gap-14">
        <form onSubmit={submit} className="card-lux p-6 sm:p-10 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className="label" htmlFor="c-name">Name</label><input id="c-name" required value={f.name} onChange={set("name")} className="field" data-testid="contact-name-input" /></div>
            <div><label className="label" htmlFor="c-email">Email</label><input id="c-email" type="email" required value={f.email} onChange={set("email")} className="field" data-testid="contact-email-input" /></div>
          </div>
          <div><label className="label" htmlFor="c-subject">Subject</label><input id="c-subject" value={f.subject} onChange={set("subject")} className="field" data-testid="contact-subject-input" /></div>
          <div><label className="label" htmlFor="c-msg">Message</label><textarea id="c-msg" required rows={6} value={f.message} onChange={set("message")} className="field" data-testid="contact-message-input" /></div>
          <button disabled={busy} className="btn-gold" data-testid="contact-submit-btn">{busy ? "Sending..." : "Send message"}</button>
        </form>
        <div>
          <span className="eyebrow">Support</span>
          <p className="flex items-center gap-3 text-slate-200"><Mail size={16} className="text-[#D4AF37]" />{settings?.contact?.email}</p>
          <p className="flex items-center gap-3 text-slate-400 mt-3"><Clock size={16} className="text-[#D4AF37]" />{settings?.contact?.support_hours}</p>
          <span className="eyebrow mt-12">FAQ</span>
          <Accordion type="single" collapsible data-testid="faq-list">
            {faqs.map((x, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-white/10">
                <AccordionTrigger className="text-left text-white hover:no-underline">{x.q}</AccordionTrigger>
                <AccordionContent className="text-slate-400">{x.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div id="privacy" className="mt-12 text-sm text-slate-400 leading-relaxed">
            <span className="eyebrow">Privacy & cookies</span>
            We only count page visits with your consent, using first-party analytics. Payments are processed securely by Stripe; card details never touch our servers.
            <button onClick={() => { localStorage.removeItem("tml_consent"); window.location.reload(); }} className="block mt-3 text-gold-light underline" data-testid="reset-consent-btn">Change cookie preference</button>
          </div>
        </div>
      </div>
    </div>
  );
}
